/**
 * Boost — thrust, consumption, the minimum-burst rule, and the 34 boost pads.
 *
 * Three separable pieces live here:
 *
 *   1. `stepBoost`  — per-car, per-tick: decides whether a boost burst is
 *      running, consumes boost, and adds thrust along the car's forward axis.
 *   2. `updateSupersonic` — the supersonic flag with RocketSim's hysteresis.
 *      It lives here rather than in the driving model because the flag is a
 *      pure function of speed, and because the demolition rule (see ./demo)
 *      keys off the FLAG, not off an instantaneous speed test.
 *   3. `BoostPadManager` — the 34 pads, their pickup volumes and their
 *      respawn timers.
 *
 * The rule that is felt in gameplay and easy to miss: BOOST_MIN_TIME. Once a
 * burst starts it runs for at least 0.1 s even if the button is released on the
 * next tick, so a one-tick tap costs 0.1 * 100/3 = 3.333 boost, not one tick's
 * 0.278. This is why boost cannot be feathered arbitrarily fast, and why
 * "tapping" boost is a real resource decision.
 * [1-src: RS `BOOST_MIN_TIME`; RU has no equivalent.]
 *
 * The burst state (is a burst running, and how long has it run) lives on
 * CarState as `car.boostBurst`, so a cloned or serialized car keeps it.
 * `boostBurst()` is a thin reader over that field — it used to be a lookup in a
 * module-private WeakMap, which is exactly what made a car non-cloneable.
 * `resetBoostBurst()` clears the burst on kickoff or respawn.
 *
 * THE BOOST MUTATOR lives here too, as four numbers on `ResolvedPhysics` (see
 * ./mutators) rather than as a mode enum this file switches on: `unlimited` is
 * a consumption rate of zero, `none` is a tank capacity of zero plus a hard
 * `boostEnabled` gate, and `recharge` is a rate and a delay. With the default
 * mutators those fields hold exactly the constants this file used to read, so
 * the default path is unchanged to the bit.
 */

import { BOOST_PADS, type BoostPad } from './arena-data';
import type { BoostBurstState, CarInput, CarState } from './car-state';
import {
  BOOST_ACCELERATION,
  BOOST_ACCELERATION_AIR,
  BOOST_MIN_TIME,
  BOOST_PAD_BIG_AMOUNT,
  BOOST_PAD_BIG_RESPAWN,
  BOOST_PAD_SMALL_AMOUNT,
  BOOST_PAD_SMALL_RESPAWN,
  BOOST_PAD_VOLUME,
  CAR_MAX_SPEED,
  SUPERSONIC_MAINTAIN_MAX_TIME,
  SUPERSONIC_MAINTAIN_MIN_SPEED,
  SUPERSONIC_THRESHOLD,
} from './constants';
import { DEFAULT_PHYSICS, type ResolvedPhysics } from './mutators';
import { addScaledTo, clampLengthBy, col, length, type Vec3 } from './math';

/**
 * Slack on the two time comparisons below (burst length and pad respawn).
 * Both timers are accumulated one dt at a time, so `12 * (1/120)` can land a
 * few ulps either side of `0.1`; without this the minimum burst would
 * occasionally last 13 ticks instead of 12 and a pad would respawn one tick
 * late. 1e-9 s is twelve orders of magnitude below a tick, so it can never
 * shift a decision by a whole tick.
 */
const TIME_EPSILON = 1e-9;

// ---------------------------------------------------------------------------
// Per-car boost burst state
// ---------------------------------------------------------------------------

export type BoostBurst = BoostBurstState;

/** The car's boost-burst state. A live reference into the car, not a copy. */
export const boostBurst = (car: CarState): BoostBurst => car.boostBurst;

/** Clear a car's burst state — kickoff, respawn, or any hard state reset.
 *  The recharge delay resets with it: a car placed on a fresh spawn has not
 *  boosted, so it should not have to wait to start refilling. */
export const resetBoostBurst = (car: CarState): void => {
  car.boostBurst.active = false;
  car.boostBurst.time = 0;
  car.boostRechargeTimer = 0;
};

// ---------------------------------------------------------------------------
// Thrust and consumption
// ---------------------------------------------------------------------------

/** Boost thrust for the car's current contact state, in uu/s^2.
 *  [confirmed: RS `BOOST_ACCEL_GROUND` 991.667 / `BOOST_ACCEL_AIR` 1058.333;
 *   the air value is a conflict resolved in favour of RS+BW over RU's 1060 —
 *   see the note on BOOST_ACCELERATION_AIR in ./constants.] */
export const boostAcceleration = (car: CarState): number =>
  car.onGround ? BOOST_ACCELERATION : BOOST_ACCELERATION_AIR;

/**
 * One tick of boost for one car. Mutates `car.velocity`, `car.boostAmount`,
 * `car.supersonic` and `car.supersonicTimer`.
 *
 * Order of operations, matching RocketSim's Car update:
 *   1. start a burst if the button is down and there is boost to burn;
 *   2. end a burst only once BOOST_MIN_TIME has elapsed;
 *   3. while the burst runs, consume `physics.boostConsumptionRate * dt`
 *      (capped by what is left) and add `boostAcceleration(car) * dt` along
 *      forward;
 *   4. recharge the tank, if the recharge mutator is on;
 *   5. clamp the car to CAR_MAX_SPEED and update the supersonic flag.
 *
 * Thrust is applied at full strength on the last, partial tick of a burst —
 * the consumption is capped, the force is not. The alternative (scaling thrust
 * by the fraction of boost actually available) is not what either simulator
 * does and would make the final tick of a boost tail off invisibly.
 *
 * The clamp to CAR_MAX_SPEED is applied unconditionally, not only while
 * boosting: it is the car's ONLY linear attenuation — there is no air drag on
 * the car body in either simulator (`CAR_AIR_DRAG = 0`). Because the clamp
 * rescales rather than zeroes, a car held at 2300 with boost stays at exactly
 * 2300 instead of creeping past it.
 */
export const stepBoost = (
  car: CarState,
  input: CarInput,
  dt: number,
  physics: ResolvedPhysics = DEFAULT_PHYSICS
): void => {
  const burst = car.boostBurst;

  // A demolished car has no body to push. Drop the burst so the respawned car
  // does not inherit a half-finished one.
  if (car.demolished) {
    burst.active = false;
    burst.time = 0;
    return;
  }

  if (physics.boostEnabled && input.boost && !burst.active && car.boostAmount > 0) {
    burst.active = true;
    burst.time = 0;
  }

  // Releasing the button only ends the burst once the minimum has elapsed.
  if (burst.active && !input.boost && burst.time + TIME_EPSILON >= BOOST_MIN_TIME) {
    burst.active = false;
  }

  if (burst.active) {
    // Zero under the `unlimited` mutator, which is the entirety of that mode:
    // the burst, the minimum time and the thrust are all untouched, the tank
    // simply never goes down.
    const used = Math.min(car.boostAmount, physics.boostConsumptionRate * dt);
    car.boostAmount -= used;
    if (car.boostAmount < 0) car.boostAmount = 0;

    addScaledTo(car.velocity, col(car.orientation, 0), boostAcceleration(car) * dt);

    burst.time += dt;

    // Running dry ends the burst regardless of the minimum time — there is
    // nothing left to spend.
    if (car.boostAmount <= 0) burst.active = false;
  }

  stepBoostRecharge(car, dt, physics);

  clampLengthBy(car.velocity, CAR_MAX_SPEED);
  updateSupersonic(car, dt);
};

/**
 * The RECHARGE boost mutator: the tank refills at RECHARGE_BOOST_PER_SECOND
 * once the car has gone RECHARGE_BOOST_DELAY without boosting.
 * [1-src: RS `Car::_UpdateBoost`'s rumble-recharge branch and its two
 *  constants, which ./constants already carries.]
 *
 * Off by default and the guard is one compare against a field that is 0 in
 * every other mode, so the default tick pays a predictable branch and nothing
 * else. `boostRechargeTimer` is only ever written from inside that branch, so a
 * default world serializes it as the 0 it was born with.
 */
const stepBoostRecharge = (car: CarState, dt: number, physics: ResolvedPhysics): void => {
  if (physics.boostRechargeRate <= 0) return;

  if (car.boostBurst.active) {
    car.boostRechargeTimer = 0;
    return;
  }

  car.boostRechargeTimer += dt;
  if (car.boostRechargeTimer + TIME_EPSILON < physics.boostRechargeDelay) return;
  if (car.boostAmount >= physics.boostMax) return;

  car.boostAmount = Math.min(physics.boostMax, car.boostAmount + physics.boostRechargeRate * dt);
};

/**
 * The supersonic flag, with RocketSim's hysteresis. Transcribed from the
 * branch structure documented on SUPERSONIC_MAINTAIN_MIN_SPEED in ./constants:
 *
 *   if (supersonic && supersonicTime < 1.0) supersonic = speed >= 2100;
 *   else                                    supersonic = speed >= 2200;
 *   supersonic ? supersonicTime += dt : supersonicTime = 0;
 *
 * Note `supersonicTimer` here ACCUMULATES over the whole supersonic period
 * rather than counting down from the moment speed drops below 2200 — that is
 * RocketSim's model, and it is what makes the grace window expire even while
 * the car is still above 2200. (The field comment in car-state.ts says
 * "seconds remaining"; the constants file and RS are authoritative and this is
 * an accumulator.)
 *
 * [1-src: RS. RU never computes this flag at all — it copies it out of the
 *  game packet.]
 */
export const updateSupersonic = (car: CarState, dt: number): void => {
  const speed = length(car.velocity);
  const maintaining = car.supersonic && car.supersonicTimer < SUPERSONIC_MAINTAIN_MAX_TIME;
  car.supersonic = speed >= (maintaining ? SUPERSONIC_MAINTAIN_MIN_SPEED : SUPERSONIC_THRESHOLD);
  car.supersonicTimer = car.supersonic ? car.supersonicTimer + dt : 0;
};

// ---------------------------------------------------------------------------
// Boost pads
// ---------------------------------------------------------------------------

/** Boost granted by a pad, before the clamp to BOOST_MAX.
 *  [confirmed: RS `BoostPads::BOOST_AMOUNT_BIG` / `_SMALL`, BW, RG] */
export const boostPadAmount = (pad: BoostPad): number =>
  pad.isBig ? BOOST_PAD_BIG_AMOUNT : BOOST_PAD_SMALL_AMOUNT;

/** Seconds before a picked-up pad comes back.
 *  [confirmed: RS `BoostPads::COOLDOWN_BIG` / `_SMALL`, BW, RG] */
export const boostPadRespawnTime = (pad: BoostPad): number =>
  pad.isBig ? BOOST_PAD_BIG_RESPAWN : BOOST_PAD_SMALL_RESPAWN;

/** Cylinder radius of the pad's pickup volume. Big and small pads share the
 *  same cylinder HEIGHT (95) and differ only in radius (208 / 144). */
export const boostPadRadius = (pad: BoostPad): number =>
  pad.isBig ? BOOST_PAD_VOLUME.cylRadiusBig : BOOST_PAD_VOLUME.cylRadiusSmall;

/**
 * Is the car's ORIGIN inside the pad's pickup cylinder?
 *
 *   |car.z - pad.z| < cylHeight            (95, both pad sizes)
 *   hypot(dx, dy)   < cylRadius            (208 big, 144 small)
 *
 * [1-src: RS namespace `BoostPads`]
 * [conflict: BW publishes heights of 168 (big) / 165 (small) with no stated
 *  measurement convention; the RADII agree across both sources. We take RS's
 *  95 — see docs/CONSTANTS_SOURCES.md §U9. The practical difference is whether
 *  a pad can be collected while jumping over it.]
 *
 * RocketSim also has a second, BOX volume (half-extents 160/120, height 64)
 * tested against the car's full AABB, but only for the car that was previously
 * locked to that pad. We do not model the lock, so the box volume is unused;
 * its constants stay exported in ./constants so nobody re-derives them.
 */
export const inBoostPadVolume = (pad: BoostPad, carPosition: Vec3): boolean => {
  if (Math.abs(carPosition.z - pad.position.z) >= BOOST_PAD_VOLUME.cylHeight) return false;
  const dx = carPosition.x - pad.position.x;
  const dy = carPosition.y - pad.position.y;
  const r = boostPadRadius(pad);
  return dx * dx + dy * dy < r * r;
};

/** Live state of one pad. */
export interface BoostPadState {
  /** Index into BOOST_PADS — the canonical RLBot FieldInfo order. */
  readonly index: number;
  readonly pad: BoostPad;
  /** False while the pad is respawning. */
  active: boolean;
  /** Seconds until this pad comes back; 0 while active. */
  respawnTimer: number;
}

/** What a car actually gained from a pad on a given tick. */
export interface BoostPickup {
  pad: BoostPadState;
  car: CarState;
  /** Boost added AFTER the clamp to BOOST_MAX, so it can be less than the
   *  pad's nominal amount. */
  amount: number;
}

/**
 * The arena's 34 boost pads: pickup tests, respawn timers, and a `reset()` for
 * kickoff.
 *
 * `step()` runs respawns BEFORE pickups, so a pad whose timer expires this tick
 * is immediately collectable — including by a car that has been parked on it,
 * which is the real game's behaviour.
 */
export class BoostPadManager {
  readonly pads: BoostPadState[];

  /**
   * Pad geometry flattened for the pickup loop: x, y, z and the SQUARED pickup
   * radius, one entry per pad. The loop runs 34 pads by N cars every tick and
   * chasing `state.pad.position.x` through three object hops for each of them
   * was 5 % of the whole simulation. Identical arithmetic, contiguous memory.
   */
  private readonly padX: Float64Array;
  private readonly padY: Float64Array;
  private readonly padZ: Float64Array;
  private readonly padR2: Float64Array;

  constructor(pads: readonly BoostPad[] = BOOST_PADS) {
    this.pads = pads.map((pad, index) => ({ index, pad, active: true, respawnTimer: 0 }));

    const n = pads.length;
    this.padX = new Float64Array(n);
    this.padY = new Float64Array(n);
    this.padZ = new Float64Array(n);
    this.padR2 = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const pad = pads[i];
      this.padX[i] = pad.position.x;
      this.padY[i] = pad.position.y;
      this.padZ[i] = pad.position.z;
      const r = boostPadRadius(pad);
      this.padR2[i] = r * r;
    }
  }

  /** Restore every pad — kickoff, goal reset, or a fresh episode. */
  reset(): void {
    for (const state of this.pads) {
      state.active = true;
      state.respawnTimer = 0;
    }
  }

  /** Number of pads currently available. */
  get activeCount(): number {
    let n = 0;
    for (const state of this.pads) if (state.active) n++;
    return n;
  }

  /**
   * Advance respawn timers, then award any pickups. Mutates `car.boostAmount`
   * for every car that collects. Returns the pickups that happened, so a
   * caller can drive audio/VFX without re-scanning.
   *
   * A car does NOT consume a pad it cannot benefit from: at the tank's maximum
   * the pad is left standing. Demolished cars are skipped entirely.
   *
   * That rule is also how the boost mutators reach the pads without a second
   * switch: under `unlimited` every car spawns full, so no pad is ever taken,
   * and under `none` the maximum is 0 and every car is already at it.
   */
  step(
    cars: readonly CarState[],
    dt: number,
    physics: ResolvedPhysics = DEFAULT_PHYSICS
  ): BoostPickup[] {
    for (const state of this.pads) {
      if (state.active) continue;
      state.respawnTimer -= dt;
      if (state.respawnTimer <= TIME_EPSILON) {
        state.active = true;
        state.respawnTimer = 0;
      }
    }

    const pickups: BoostPickup[] = [];
    for (let p = 0; p < this.pads.length; p++) {
      const state = this.pads[p];
      if (!state.active) continue;
      // Same predicate as `inBoostPadVolume`, read out of the flat arrays.
      const px = this.padX[p];
      const py = this.padY[p];
      const pz = this.padZ[p];
      const r2 = this.padR2[p];
      for (let ci = 0; ci < cars.length; ci++) {
        const car = cars[ci];
        if (car.demolished) continue;
        if (car.boostAmount >= physics.boostMax) continue;
        const cp = car.position;
        if (Math.abs(cp.z - pz) >= BOOST_PAD_VOLUME.cylHeight) continue;
        const dx = cp.x - px;
        const dy = cp.y - py;
        if (dx * dx + dy * dy >= r2) continue;

        const before = car.boostAmount;
        car.boostAmount = Math.min(physics.boostMax, before + boostPadAmount(state.pad));
        state.active = false;
        state.respawnTimer = boostPadRespawnTime(state.pad);
        pickups.push({ pad: state, car, amount: car.boostAmount - before });
        // One pad can only be taken by one car per tick.
        break;
      }
    }
    return pickups;
  }
}
